import { Component, OnInit, inject } from '@angular/core';

import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MarketplaceBid, MarketplaceListing } from '@models';
import { MarketplaceBidService, MarketplaceListingService, ToastService } from '@services';
import { DocsFacade, WorkspaceFacade, UserFacade } from '@state';

@Component({
  selector: 'app-marketplace',
  templateUrl: './marketplace.page.html',
  styleUrls: ['./marketplace.page.scss'],
  standalone: true,
  imports: [RouterModule, FormsModule],
})
export class MarketplacePage implements OnInit {
  private userStore = inject(UserFacade);
  activeTab: 'browse' | 'myBids' | 'myListings' = 'browse';

  categories = ['Web', 'Mobile', 'API', 'DevOps', 'AI/ML', 'Data'];

  filters = {
    query: '',
    category: '',
    remoteOnly: false,
    budgetMin: 0,
    budgetMax: 0,
  };

  loading = false;
  savingListing = false;
  savingBid = false;
  currentUserId: string = '';

  listings: MarketplaceListing[] = [];

  bids: MarketplaceBid[] = [];

  // Create / edit listing
  showListingForm = false;
  editingListingId: string | null = null;
  listingDraft: any = this.emptyListingDraft();

  // Inline bid UI on cards
  biddingListingId: string | null = null;
  bidAmount: number = 0;
  bidMessage: string = '';

  setTab(tab: 'browse' | 'myBids' | 'myListings') {
    this.activeTab = tab;
    if (tab === 'myListings') {
      this.showListingForm = false;
      this.editingListingId = null;
    }
  }

  get displayedProjects() {
    if (this.activeTab === 'myListings') {
      return this.applyFilters(this.listings.filter((p) => (p.ownerId || (p as any).createdBy) === this.currentUserId));
    }
    return this.applyFilters(this.listings.filter((p) => (p.status ?? 'open') === 'open'));
  }

  get myBidsView() {
    const byId = new Map(this.listings.map((l) => [l.id, l]));
    return (this.bids ?? [])
      .filter((b) => b.bidderId === this.currentUserId)
      .map((b) => ({
        bid: b,
        listing: byId.get(b.listingId) ?? null,
      }))
      .sort((a, b) => ((b.bid as any)?.created?.getTime?.() ?? 0) - ((a.bid as any)?.created?.getTime?.() ?? 0));
  }

  private applyFilters(list: MarketplaceListing[]) {
    return list.filter((p) => {
      if (this.filters.query) {
        const q = this.filters.query.toLowerCase();
        if (!p.title.toLowerCase().includes(q) && !p.description.toLowerCase().includes(q)) return false;
      }
      if (this.filters.category && p.category !== this.filters.category) return false;
      if (this.filters.remoteOnly && !p.remote) return false;
      if (this.filters.budgetMin && p.budget < this.filters.budgetMin) return false;
      if (this.filters.budgetMax && p.budget > this.filters.budgetMax) return false;
      return true;
    });
  }

  toggleSave(_listingId: string) {
    this.toast.info('Saved items will be persisted soon');
  }

  startBid(listingId: string) {
    this.biddingListingId = listingId;
    this.bidAmount = 0;
    this.bidMessage = '';
  }

  viewProject(listingId: string) {
    // Placeholder: route to project view/details when available
    this.router.navigate(['/project'], { queryParams: { from: 'marketplace', id: listingId } });
  }

  openCreateListing() {
    this.activeTab = 'myListings';
    this.showListingForm = true;
    this.editingListingId = null;
    this.listingDraft = this.emptyListingDraft();
  }

  constructor(
    private router: Router,
    private listingsService: MarketplaceListingService,
    private bidsService: MarketplaceBidService,
    private toast: ToastService,
    private store: WorkspaceFacade,
  ) {}

  private docs = inject(DocsFacade);

  async ngOnInit(): Promise<void> {
    this.currentUserId = this.userStore.current()?.id ?? '';
    await Promise.all([this.loadListings(), this.loadBids()]);
    const pid = this.store.activeProjectId();
    if (pid) {
      // Mark marketplace as visited/initialized for workflow status.
      await this.docs.upsert(pid, 'marketplace.state', { loadedAt: Date.now() });
    }
  }

  private async loadListings(): Promise<void> {
    this.loading = true;
    try {
      // Marketplace browse should be global (not scoped to the current org).
      const res = await this.listingsService.find();
      this.listings = (res.data ?? []) as MarketplaceListing[];
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to load marketplace listings', 'Marketplace');
      this.listings = [];
    } finally {
      this.loading = false;
    }
  }

  private async loadBids(): Promise<void> {
    try {
      if (!this.currentUserId) this.currentUserId = this.userStore.current()?.id ?? '';
      if (!this.currentUserId) return;
      const res = await this.bidsService.find({ where: { bidderId: this.currentUserId } as any });
      this.bids = (res.data ?? []) as MarketplaceBid[];
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to load bids', 'Marketplace');
      this.bids = [];
    }
  }

  private emptyListingDraft() {
    return {
      title: '',
      organizationName: '',
      category: '',
      remote: true,
      budget: 0,
      timeframeWeeks: 0,
      tagsText: '',
      description: '',
      requirementsText: '',
      deliverablesText: '',
      techStackText: '',
      commitmentHoursPerWeek: null as number | null,
      timezone: '',
      startDate: '',
    };
  }

  private toList(text: string): string[] {
    return (text || '')
      .split('\n')
      .map((x) => x.trim())
      .filter(Boolean);
  }

  private toTags(text: string): string[] {
    return (text || '')
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);
  }

  async submitListing(): Promise<void> {
    if (this.savingListing) return;
    if (!this.currentUserId) {
      this.currentUserId = this.userStore.current()?.id ?? '';
    }
    if (!this.currentUserId) {
      this.toast.error('You must be logged in to create a listing', 'Marketplace');
      return;
    }
    const title = (this.listingDraft.title || '').trim();
    const description = (this.listingDraft.description || '').trim();
    if (!title || !description) {
      this.toast.error('Title and description are required', 'Marketplace');
      return;
    }

    this.savingListing = true;
    try {
      const orgId = this.store.selectedOrganizationId() ?? undefined;
      const orgName =
        (this.store.organizations()?.find((o: any) => o.id === orgId)?.name as string | undefined) ??
        (this.listingDraft.organizationName || '').trim();

      const entity = this.editingListingId
        ? (this.listings.find((l) => l.id === this.editingListingId) ?? new MarketplaceListing())
        : new MarketplaceListing();

      entity.ownerId = this.currentUserId;
      entity.organizationId = orgId;
      entity.organizationName = orgName || '';
      entity.title = title;
      entity.description = description;
      entity.category = (this.listingDraft.category || '').trim();
      entity.remote = Boolean(this.listingDraft.remote);
      entity.budget = Number(this.listingDraft.budget || 0);
      entity.timeframeWeeks = Number(this.listingDraft.timeframeWeeks || 0);
      entity.tags = this.toTags(this.listingDraft.tagsText);
      entity.requirements = this.toList(this.listingDraft.requirementsText);
      entity.deliverables = this.toList(this.listingDraft.deliverablesText);
      entity.techStack = this.toTags(this.listingDraft.techStackText);
      entity.commitmentHoursPerWeek =
        this.listingDraft.commitmentHoursPerWeek === null || this.listingDraft.commitmentHoursPerWeek === ''
          ? undefined
          : Number(this.listingDraft.commitmentHoursPerWeek);
      entity.timezone = (this.listingDraft.timezone || '').trim() || undefined;
      entity.startDate = (this.listingDraft.startDate || '').trim() || undefined;
      entity.status = entity.status ?? 'open';

      await this.listingsService.save(entity);
      this.toast.success(this.editingListingId ? 'Listing updated' : 'Listing created', 'Marketplace');
      this.showListingForm = false;
      this.editingListingId = null;
      this.listingDraft = this.emptyListingDraft();
      await this.loadListings();
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to save listing', 'Marketplace');
    } finally {
      this.savingListing = false;
    }
  }

  editListing(listing: MarketplaceListing) {
    this.activeTab = 'myListings';
    this.showListingForm = true;
    this.editingListingId = listing.id;
    this.listingDraft = {
      title: listing.title || '',
      organizationName: listing.organizationName || '',
      category: listing.category || '',
      remote: Boolean(listing.remote),
      budget: listing.budget || 0,
      timeframeWeeks: listing.timeframeWeeks || 0,
      tagsText: (listing.tags || []).join(', '),
      description: listing.description || '',
      requirementsText: (listing.requirements || []).join('\n'),
      deliverablesText: (listing.deliverables || []).join('\n'),
      techStackText: (listing.techStack || []).join(', '),
      commitmentHoursPerWeek: (listing.commitmentHoursPerWeek as any) ?? null,
      timezone: listing.timezone || '',
      startDate: listing.startDate || '',
    };
  }

  cancelListingForm() {
    this.showListingForm = false;
    this.editingListingId = null;
    this.listingDraft = this.emptyListingDraft();
  }

  async setListingStatus(listing: MarketplaceListing, status: 'open' | 'paused' | 'closed') {
    try {
      const updated = { ...(listing as any), status } as MarketplaceListing;
      await this.listingsService.save(updated);
      await this.loadListings();
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to update listing', 'Marketplace');
    }
  }

  async deleteListing(listing: MarketplaceListing) {
    try {
      await this.listingsService.delete(listing.id);
      await this.loadListings();
      this.toast.success('Listing deleted', 'Marketplace');
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to delete listing', 'Marketplace');
    }
  }

  async submitBid(listing: MarketplaceListing) {
    if (this.savingBid) return;
    if (!this.currentUserId) {
      this.currentUserId = this.userStore.current()?.id ?? '';
    }
    if (!this.currentUserId) {
      this.toast.error('You must be logged in to bid', 'Marketplace');
      return;
    }
    const amount = Number(this.bidAmount || 0);
    if (!amount || amount <= 0) {
      this.toast.error('Bid amount must be greater than 0', 'Marketplace');
      return;
    }
    this.savingBid = true;
    try {
      const existing = this.bids.find((b) => b.listingId === listing.id && b.bidderId === this.currentUserId) ?? null;
      const bid = existing ? existing : new MarketplaceBid();
      bid.listingId = listing.id;
      bid.bidderId = this.currentUserId;
      bid.amount = amount;
      bid.message = (this.bidMessage || '').trim() || undefined;
      bid.status = bid.status ?? 'Pending';
      await this.bidsService.save(bid);
      this.toast.success(existing ? 'Bid updated' : 'Bid submitted', 'Marketplace');
      this.biddingListingId = null;
      this.bidAmount = 0;
      this.bidMessage = '';
      await this.loadBids();
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to submit bid', 'Marketplace');
    } finally {
      this.savingBid = false;
    }
  }

  cancelBid() {
    this.biddingListingId = null;
    this.bidAmount = 0;
    this.bidMessage = '';
  }

  async withdrawBid(bid: MarketplaceBid) {
    try {
      await this.bidsService.delete(bid.id);
      await this.loadBids();
      this.toast.success('Bid withdrawn', 'Marketplace');
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to withdraw bid', 'Marketplace');
    }
  }
} 